import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  useRouteDocumentTitle,
} from '@client/toolkit';

import {
  BuildingFilterPanel,
  KpiTile,
  PLACEHOLDER,
  ReportTableSkeletonRows,
  formatNumber,
  formatPercent,
} from '../components/portfolio-report';
import { ReportingPaths } from '../reporting.router';
import { ReportPageContent } from '../../../components/layout/report-page-content';
import { YearPicker } from '../../../components';
import { useYearParam } from '../../../hooks';
import { FinancingCompanyRow, useGetReportingFinancingQuery } from '@client/api';

// Balance-sheet figures are large for SPVs, so the report shows amounts in millions (MNOK).
function formatMillions(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return PLACEHOLDER;
  return formatNumber(value / 1_000_000, 1);
}

export function ReportingFinancingRoute() {
  const { t } = useTranslation('reporting');
  useRouteDocumentTitle(t('financing.documentTitle'), ReportingPaths.Financing);

  const { selectedYear, setSelectedYear } = useYearParam();
  const [search, setSearch] = useState('');

  // Figures follow the selected year (balance = latest period on or before the year end). We use
  // isFetching so skeletons also show while refetching after a year change.
  const { data, isFetching } = useGetReportingFinancingQuery({
    startDate: selectedYear.startOf('year').format('YYYY-MM-DD'),
    endDate: selectedYear.endOf('year').format('YYYY-MM-DD'),
  });

  const companies = useMemo(() => data?.companies ?? [], [data]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (query === '') return companies;
    return companies.filter((c) =>
      (c.name ?? '').toLowerCase().includes(query) || (c.organizationNumber ?? '').includes(query),
    );
  }, [companies, search]);

  const summary = data?.summary;

  const renderStatus = (row: FinancingCompanyRow) => {
    if (row.status === 'Synced') {
      const date = row.statusPeriodEnd ? dayjs(row.statusPeriodEnd).format('DD.MM.YYYY') : null;
      return (
        <span className="text-emerald-300">
          {date ? t('financing.status.synced', { date }) : t('financing.status.syncedNoDate')}
        </span>
      );
    }
    return (
      <span className="text-rose-300">
        {t('financing.status.missing', { quarter: row.firstMissingQuarter ?? 1 })}
      </span>
    );
  };

  const filterPanel = (
    <BuildingFilterPanel
      title={t('financing.filter.title')}
      searchPlaceholder={t('financing.filter.company')}
      noTagsLabel=""
      buildingFilter={search}
      onBuildingFilterChange={setSearch}
      allTags={[]}
      activeTags={[]}
      onToggleTag={() => undefined}
      action={<YearPicker value={selectedYear} onChange={setSelectedYear} />}
    />
  );

  return (
    <ReportPageContent breadcrumb={t('financing.breadcrumb')} menu={filterPanel}>
      <div className="flex gap-5 justify-between h-[146px] text-white">
        <KpiTile
          label={t('financing.kpi.companies')}
          sub={t('financing.kpi.companiesSub', { count: summary?.buildingCount ?? 0 })}
          value={formatNumber(summary?.companyCount ?? 0)}
          loading={isFetching}
        />
        <KpiTile
          label={t('financing.kpi.debtRatio')}
          sub={t('financing.kpi.debtRatioSub')}
          tooltip={t('financing.kpi.debtRatioTooltip')}
          value={formatNumber(summary?.debtRatio, 2)}
          loading={isFetching}
        />
        <KpiTile
          label={t('financing.kpi.equityRatio')}
          sub={t('financing.kpi.equityRatioSub')}
          tooltip={t('financing.kpi.equityRatioTooltip')}
          value={formatPercent(summary?.equityRatioPercent)}
          loading={isFetching}
        />
        <KpiTile
          label={t('financing.kpi.icr')}
          sub={t('financing.kpi.icrSub')}
          tooltip={t('financing.kpi.icrTooltip')}
          value={formatNumber(summary?.interestCoverageRatio, 2)}
          loading={isFetching}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle variant="highlighted">{t('financing.table.title')}</CardTitle>
        </CardHeader>
        <CardContent variant="fill" className="px-4 pb-4">
          <Table className="w-full" size="small">
            <TableHead>
              <TableHeadRow>
                <TableHeadCell>{t('financing.table.header.company')}</TableHeadCell>
                <TableHeadCell>{t('financing.table.header.group')}</TableHeadCell>
                <TableHeadCell align="right">{t('financing.table.header.buildings')}</TableHeadCell>
                <TableHeadCell>{t('financing.table.header.regime')}</TableHeadCell>
                <TableHeadCell align="right">{t('financing.table.header.assets')}</TableHeadCell>
                <TableHeadCell align="right">{t('financing.table.header.liabilities')}</TableHeadCell>
                <TableHeadCell align="right">{t('financing.table.header.interestBearingDebt')}</TableHeadCell>
                <TableHeadCell align="right">{t('financing.table.header.equity')}</TableHeadCell>
                <TableHeadCell align="right">{t('financing.table.header.debtRatio')}</TableHeadCell>
                <TableHeadCell align="right">{t('financing.table.header.equityRatio')}</TableHeadCell>
                <TableHeadCell align="right">{t('financing.table.header.icr')}</TableHeadCell>
                <TableHeadCell>{t('financing.table.header.status')}</TableHeadCell>
              </TableHeadRow>
            </TableHead>
            <TableBody>
              {isFetching ? (
                <ReportTableSkeletonRows columns={12} />
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12}>
                    <span className="text-white/60">{t('financing.table.empty')}</span>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((row, index) => (
                  <TableRow key={row.organizationNumber ?? `no-company-${index}`}>
                    <TableCell>
                      <span className="font-medium text-white">{row.name ?? t('financing.noCompany')}</span>
                      {row.organizationNumber ? (
                        <span className="block text-xs text-white/50">{row.organizationNumber}</span>
                      ) : null}
                    </TableCell>
                    <TableCell>{row.groupParentName ?? PLACEHOLDER}</TableCell>
                    <TableCell align="right">{formatNumber(row.buildingCount)}</TableCell>
                    <TableCell>{t(`financing.regime.${row.regime}`)}</TableCell>
                    <TableCell align="right">{formatMillions(row.totalAssets)}</TableCell>
                    <TableCell align="right">{formatMillions(row.totalLiabilities)}</TableCell>
                    <TableCell align="right">{formatMillions(row.interestBearingDebt)}</TableCell>
                    <TableCell align="right">{formatMillions(row.equity)}</TableCell>
                    <TableCell align="right">{formatNumber(row.debtRatio, 2)}</TableCell>
                    <TableCell align="right">{formatPercent(row.equityRatioPercent)}</TableCell>
                    <TableCell align="right">{formatNumber(row.interestCoverageRatio, 2)}</TableCell>
                    <TableCell>{renderStatus(row)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </ReportPageContent>
  );
}
